"use client";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import useCheckScreen from "../../../hooks/useCheckScreen/index.js";
import "./viewer.css";
let videojs;
const COMPONENT_NAME = "VideoPlayer";
const DEFAULT_ASPECT_RATIO = "16:9";
const loadVideoJS = async () => {
  videojs = (await import("video.js")).default;
  await import("video.js/dist/video-js.css");
};
const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};
const updatePlayButtonAriaLabel = ({
  playButtonAriaLabel,
  duration
}) => {
  let ariaLabel = playButtonAriaLabel;
  if (duration) {
    ariaLabel += ` ${duration}`;
  }
  const playButtonElement = document.querySelector(".vjs-big-play-button");
  playButtonElement == null ? void 0 : playButtonElement.setAttribute("aria-label", ariaLabel);
};
const VideoAnalyticTracker = ({
  player,
  analytics,
  options = {}
}) => {
  const [progressTriggers, setProgressTriggers] = useState({
    muted: options.muted,
    start: false,
    "25": false,
    "50": false,
    "75": false,
    "100": false
  });
  const { autoReplay } = options;
  useEffect(() => {
    var _a, _b;
    if (player) {
      const handleTimeUpdate = () => {
        var _a2, _b2, _c, _d;
        if ((analytics == null ? void 0 : analytics.onStart) || (analytics == null ? void 0 : analytics.on25PercentComplete) || (analytics == null ? void 0 : analytics.on50PercentComplete) || (analytics == null ? void 0 : analytics.on75PercentComplete) || (analytics == null ? void 0 : analytics.on100PercentComplete)) {
          const currentTime = player.currentTime() || 0;
          const duration = player.duration() || 0;
          const percentage = currentTime / duration * 100;
          if (!progressTriggers["start"] && (analytics == null ? void 0 : analytics.onStart)) {
            analytics == null ? void 0 : analytics.onStart();
            setProgressTriggers((prev) => ({ ...prev, start: true }));
          }
          if (!progressTriggers["25"] && percentage >= 25) {
            (_a2 = analytics == null ? void 0 : analytics.on25PercentComplete) == null ? void 0 : _a2.call(analytics);
            setProgressTriggers((prev) => ({ ...prev, "25": true }));
          }
          if (progressTriggers["25"] && !progressTriggers["50"] && percentage >= 50) {
            (_b2 = analytics == null ? void 0 : analytics.on50PercentComplete) == null ? void 0 : _b2.call(analytics);
            setProgressTriggers((prev) => ({ ...prev, "50": true }));
          }
          if (progressTriggers["50"] && !progressTriggers["75"] && percentage >= 75) {
            (_c = analytics == null ? void 0 : analytics.on75PercentComplete) == null ? void 0 : _c.call(analytics);
            setProgressTriggers((prev) => ({ ...prev, "75": true }));
          }
          if (progressTriggers["75"] && !progressTriggers["100"] && percentage >= 100) {
            (_d = analytics == null ? void 0 : analytics.on100PercentComplete) == null ? void 0 : _d.call(analytics);
            setProgressTriggers((prev) => ({ ...prev, "100": true }));
          }
        }
      };
      player.on("timeupdate", handleTimeUpdate);
      const handleVolumeChange = () => {
        var _a2, _b2;
        if (player.muted() && !progressTriggers.muted) {
          (_a2 = analytics == null ? void 0 : analytics.onMute) == null ? void 0 : _a2.call(analytics);
          setProgressTriggers((prev) => ({ ...prev, muted: true }));
        } else if (progressTriggers.muted) {
          (_b2 = analytics == null ? void 0 : analytics.onUnmute) == null ? void 0 : _b2.call(analytics);
          setProgressTriggers((prev) => ({ ...prev, muted: false }));
        }
      };
      player.on("volumechange", handleVolumeChange);
      const handleClick = () => {
        var _a2, _b2, _c, _d;
        if (((_b2 = (_a2 = player == null ? void 0 : player.controlBar) == null ? void 0 : _a2.playToggle) == null ? void 0 : _b2.controlText_) === "Play" && (analytics == null ? void 0 : analytics.onResume)) {
          analytics == null ? void 0 : analytics.onResume();
        }
        if (((_d = (_c = player == null ? void 0 : player.controlBar) == null ? void 0 : _c.playToggle) == null ? void 0 : _d.controlText_) === "Pause" && !player.ended() && (analytics == null ? void 0 : analytics.onPause)) {
          analytics == null ? void 0 : analytics.onPause();
        }
      };
      (_b = (_a = player == null ? void 0 : player.controlBar) == null ? void 0 : _a.playToggle) == null ? void 0 : _b.on("click", handleClick);
      const handleAutoReplay = () => {
        if (autoReplay) {
          player.currentTime(0);
          player.play();
        }
      };
      player.on("ended", handleAutoReplay);
      return () => {
        var _a2, _b2;
        player.off("timeupdate", handleTimeUpdate);
        player.off("volumechange", handleVolumeChange);
        (_b2 = (_a2 = player == null ? void 0 : player.controlBar) == null ? void 0 : _a2.playToggle) == null ? void 0 : _b2.off("click", handleClick);
        player.off("ended", handleAutoReplay);
      };
    }
  }, [autoReplay, player, analytics, progressTriggers]);
  return /* @__PURE__ */ jsx(Fragment, {});
};
const VideoPlayer = (props) => {
  const [_isPlayerRefLoaded, setIsPlayerRefLoaded] = useState(false);
  const { isMobile, isTablet } = useCheckScreen();
  const {
    analytics = {},
    controlBarStyles,
    height,
    language,
    options,
    playButtonAriaLabel = "Play Video",
    showDurationOverlay,
    width
  } = props;
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  useEffect(() => {
    var _a;
    const userActionsClickHandler = (_a = options == null ? void 0 : options.userActions) == null ? void 0 : _a.click;
    const videoClickHandler = (event) => {
      if (event.target.parentElement.tagName === "VIDEO-JS" && userActionsClickHandler && typeof userActionsClickHandler === "function") {
        userActionsClickHandler();
      }
    };
    loadVideoJS().then(() => {
      var _a2;
      if (!(playerRef == null ? void 0 : playerRef.current)) {
        const videoElement = document.createElement("video-js");
        if (options == null ? void 0 : options.autoplay) {
          videoElement.classList.add("vjs-big-play-noDisplay");
        }
        if (videoRef.current) {
          videoRef.current.appendChild(videoElement);
        }
        playerRef.current = videojs(videoElement, {
          ...options,
          language,
          html5: { nativeTextTracks: false },
          // Disables timeout of userActive state,
          // overriding any setting passed from a parent.
          // Accessibility requirements dictate that
          // video controls stay visible.
          inactivityTimeout: 0
        });
        if (showDurationOverlay) {
          playerRef.current.on("loadedmetadata", () => {
            var _a3, _b;
            const videoDuration = (_a3 = playerRef == null ? void 0 : playerRef.current) == null ? void 0 : _a3.duration();
            if (videoDuration && ((_b = playerRef.current) == null ? void 0 : _b.paused())) {
              const formattedDuration = formatTime(videoDuration);
              const durationDisplay = document.createElement("div");
              durationDisplay.className = "video-duration-overlay";
              durationDisplay.textContent = formattedDuration;
              videoElement == null ? void 0 : videoElement.appendChild(durationDisplay);
              updatePlayButtonAriaLabel({
                playButtonAriaLabel,
                duration: formattedDuration
              });
            }
          });
        }
        playerRef.current.on("play", () => {
          const overlay = videoElement == null ? void 0 : videoElement.querySelector(
            ".video-duration-overlay"
          );
          if (overlay) {
            overlay.remove();
          }
        });
        setIsPlayerRefLoaded(true);
        (isMobile || isTablet) && ((_a2 = playerRef == null ? void 0 : playerRef.current) == null ? void 0 : _a2.on(
          "touchend",
          (event) => videoClickHandler(event)
        ));
        const controlBarElement = document.querySelector(".vjs-control-bar");
        controlBarStyles && (controlBarElement == null ? void 0 : controlBarElement.setAttribute("style", controlBarStyles));
        updatePlayButtonAriaLabel({ playButtonAriaLabel });
        const controlButtons = document.querySelectorAll(
          "button.vjs-control.vjs-button"
        );
        controlButtons.forEach((controlButton) => {
          if (controlButton.classList.contains("vjs-menu-button"))
            return;
          const span = document.createElement("span");
          span.className = "vjs-accessible-button";
          controlButton.appendChild(span);
        });
      } else {
        const player = playerRef.current;
        const playerSrc = player.src(void 0);
        if (playerSrc !== options.sources.src) {
          player.src(options.sources);
          player.muted(options.muted);
        }
        player.aspectRatio(
          (options == null ? void 0 : options.aspectRatio) || DEFAULT_ASPECT_RATIO
        );
        player.autoplay(options.autoplay);
        player.controls(options.controls);
        player.fluid(options.fluid);
        player.responsive(options.responsive);
        player.poster(options.poster);
      }
    });
    return () => {
      var _a2;
      (_a2 = playerRef == null ? void 0 : playerRef.current) == null ? void 0 : _a2.off(
        "touchend",
        (event) => videoClickHandler(event)
      );
    };
  }, [options, language, isMobile, isTablet]);
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: { width, height },
      className: "video-player",
      "data-vjs-player": true,
      "data-testid": COMPONENT_NAME,
      children: [
        (playerRef == null ? void 0 : playerRef.current) && analytics && /* @__PURE__ */ jsx(
          VideoAnalyticTracker,
          {
            options,
            player: playerRef == null ? void 0 : playerRef.current,
            analytics
          }
        ),
        /* @__PURE__ */ jsx("div", { ref: videoRef })
      ]
    }
  );
};
VideoPlayer.displayName = COMPONENT_NAME;
export {
  VideoPlayer
};
